import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { ArrowDropDown, Clear } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '../DatePicker';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { GlobalUtilContext } from '../../contexts';
import { PRIORITY_TO_COLOR_MAPPING } from '../../layouts/constants';
import { FILTERS } from '../SearchFilter/constants';
import * as dayjs from 'dayjs';

function FilterPill({ filterType, filters, setFilters }) {
  const [filter, setFilter] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const { dropdownOptions } = React.useContext(GlobalUtilContext);
  const { t } = useTranslation();

  React.useEffect(() => {
    if (!Object.keys(filters)?.length && filterType) {
      if (FILTERS?.[filterType] === 'dateRange') {
        setFilter({});
      } else {
        setFilter('');
      }
      return;
    }

    if (filters?.[filterType]) {
      setFilter(filters[filterType]);
    }

    if (filterType === 'date') {
      if (filters?.single_date) {
        setFilter({
          startDate: dayjs(filters?.single_date).format('YYYY-MM-DD').replaceAll('-', '/'),
          endDate: dayjs(filters?.single_date).format('YYYY-MM-DD').replaceAll('-', '/')
        });
      }
      if (filters?.date_before && filters?.date_after) {
        setFilter({
          startDate: dayjs(filters?.date_after).format('YYYY-MM-DD').replaceAll('-', '/'),
          endDate: dayjs(filters?.date_before).format('YYYY-MM-DD').replaceAll('-', '/')
        });
      }
    }
  }, [filters, filterType]);

  const handleValueChange = React.useCallback(
    (value) => {
      if (FILTERS?.[filterType] === 'dateRange') {
        let contextValue;
        let filterValue;

        if (value?.singleDate) {
          contextValue = {
            single_date: value.singleDate,
            [`${filterType}_after`]: null,
            [`${filterType}_before`]: null
          };
          filterValue = {
            startDate: value.singleDate,
            endDate: value.singleDate
          };
        }

        if (value?.startDate) {
          contextValue = {
            [`${filterType}_after`]: value?.startDate,
            [`${filterType}_before`]: value?.endDate,
            single_date: null
          };
          filterValue = value;
        }

        setFilter(filterValue);
        setFilters({
          ...filters,
          ...contextValue
        });
      } else {
        setFilter(value?.target?.value);
        setFilters({
          ...filters,
          [filterType]: value?.target?.value
        });
      }
    },
    [filters, filterType, setFilters]
  );

  const displayValue = React.useMemo(() => {
    if (filter?.startDate || filter?.endDate) {
      if (dayjs(filter?.startDate).isSame(filter?.endDate)) {
        if (dayjs().subtract(1, 'day').isSame(filter?.startDate, 'date')) {
          return t('yesterday');
        }

        return `${filter?.startDate}`;
      }

      return `${filter?.startDate || ''} - ${filter?.endDate || ''}`;
    }

    return '';
  }, [filter]);

  const inputWidth = React.useMemo(() => {
    return (displayValue?.length || 0) * 8;
  }, [displayValue]);

  const handleClear = React.useCallback(
    (e) => {
      e?.stopPropagation();
      if (FILTERS?.[filterType] === 'dateRange') {
        const newContextValue = {
          [`${filterType}_after`]: null,
          [`${filterType}_before`]: null,
          single_date: null
        };
        setFilter({});
        setFilters({
          ...filters,
          ...newContextValue
        });
      } else {
        setFilter('');
        setFilters({
          ...filters,
          [filterType]: ''
        });
      }
    },
    [setFilter, setFilters, filterType, filters]
  );

  const renderDateRangeFilter = () => (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <Box
          sx={{
            bgcolor: '#E2E2E2',
            padding: '4px 12px',
            textAlign: 'center',
            borderRadius: '4px 0 0 4px'
          }}
        >
          <Typography color="#00010E" fontSize={14} fontWeight={200} lineHeight="22px">
            {t(`filters.${filterType}`)}
          </Typography>
        </Box>
        <TextField
          InputProps={{
            endAdornment: !displayValue ? (
              <InputAdornment position="start" sx={{ cursor: 'pointer' }}>
                <ArrowDropDown color="#9B959F" onClick={() => setIsOpen(true)} />
              </InputAdornment>
            ) : (
              <InputAdornment position="start" sx={{ cursor: 'pointer' }}>
                <Clear
                  color="#9B959F"
                  fontSize="14px"
                  onClick={handleClear}
                  sx={{ cursor: 'pointer' }}
                />
              </InputAdornment>
            )
          }}
          disabled
          id="date-range-input-fullWidth"
          onClick={() => setIsOpen(true)}
          placeholder={t('filters.select-dates')}
          sx={{
            cursor: 'pointer',
            '& .MuiInputBase-input.Mui-disabled': {
              padding: 0,
              minWidth: 80,
              cursor: 'pointer',
              WebkitTextFillColor: '#00010E',
              opacity: 1,
              fontSize: 14,
              lineHeight: '22px',
              width: `${inputWidth}px`
            },
            '& .MuiInputBase-input.Mui-disabled::placeholder': {
              padding: 0,
              WebkitTextFillColor: '#9B959F',
              opacity: 1,
              fontSize: 14,
              lineHeight: '22px',
              fontWeight: 400
            },
            '& .MuiInputBase-root': {
              borderRadius: '0 4px 4px 0',
              padding: '4px 0 4px 12px',
              cursor: 'pointer',
              background: 'white',
              border: '1px solid rgba(155,149,159,0.26)'
            },
            '& .MuiInputBase-input': {
              pointerEvents: 'none'
            }
          }}
          value={displayValue}
        />
      </Box>
      <DatePicker handleValueChange={handleValueChange} isOpen={isOpen} setIsOpen={setIsOpen} />
    </>
  );

  // TODO: Connect this with their respective options
  const renderDropDownFilter = () => (
    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
      <Box
        sx={{
          bgcolor: '#E2E2E2',
          padding: '4px 12px',
          textAlign: 'center',
          borderRadius: '4px 0 0 4px'
        }}
      >
        <Typography color="#00010E" fontSize={14} fontWeight={200} lineHeight="22px">
          {t(`filters.${filterType}`)}
        </Typography>
      </Box>
      <FormControl
        sx={{
          ' .MuiInputBase-root': {
            borderRadius: '0 4px 4px 0',
            padding: '3px 0 3px 12px',
            cursor: 'pointer',
            background: 'white',
            border: '1px solid rgba(155,149,159,0.26)'
          }
        }}
      >
        <Select
          displayEmpty
          id="filter-simple-select"
          onChange={handleValueChange}
          placeholder="ABCDEC"
          sx={{
            '& .MuiInputBase-input': {
              padding: 0,
              minWidth: 60,
              cursor: 'pointer',
              WebkitTextFillColor: !filter ? '#9B959F' : '#00010E',
              opacity: 1,
              fontSize: 14,
              lineHeight: '22px',
              width: `${inputWidth}px`
            }
          }}
          value={filter}
        >
          <MenuItem disabled value="">
            {t('filters.select')}
          </MenuItem>
          {dropdownOptions?.[filterType]?.map((option, index) => {
            if (filterType === 'priority') {
              // this one gets special styling & treatment
              return (
                <MenuItem
                  key={`dropdown-option-${filterType}-${index}`}
                  sx={{
                    color: PRIORITY_TO_COLOR_MAPPING?.[option] || '#00010E'
                  }}
                  value={option}
                >{`P${option}`}</MenuItem>
              );
            } else {
              return (
                <MenuItem key={`dropdown-option-${filterType}-${index}`} value={option}>
                  {option || 'null'}
                </MenuItem>
              );
            }
          })}
        </Select>
      </FormControl>
    </Box>
  );

  return (
    <Box sx={{ mt: 2, pl: 2 }}>
      {FILTERS?.[filterType] === 'dateRange' ? renderDateRangeFilter() : renderDropDownFilter()}
    </Box>
  );
}

FilterPill.propTypes = {
  filterType: PropTypes.string.isRequired,
  filters: PropTypes.any.isRequired,
  setFilters: PropTypes.func.isRequired
};

export { FilterPill };
